/**
 * A hash router, deliberately about twenty lines.
 *
 * Hash routing rather than the History API because the built bundle is meant to be mounted by
 * FastAPI at whatever path is convenient, and a history-routed SPA needs a catch-all rewrite
 * on the server to survive a reload. `#/image/12` survives it for free.
 */

import { asHomeTab, DEFAULT_HOME_TAB, type HomeTabId } from './homeTabs';

export type { HomeTabId };

export type Route =
  /**
   * The landing page. One route, four panels: the tab is part of the URL rather than component
   * state so a reload keeps you where you were and a link to `#/home/keyboard` opens on the
   * shortcut table for the person you sent it to.
   */
  | { name: 'home'; tab: HomeTabId }
  /**
   * The work queue. `task: null` is the chooser — "what are you sitting down to do?" — and a
   * task id is that task's queue.
   *
   * The id is **not** a union of known literals, and that is deliberate: the task registry
   * lives on the server (`GET /api/tasks`), so a client-side enumeration would be a second
   * copy of it, free to drift. The views resolve the id against the fetched list instead, and
   * an id this build does not know lands back on the chooser with a notice — the same
   * fallback shape as `asHomeTab`, for the same reason.
   */
  | { name: 'queue'; task: string | null }
  /** The corpus browser: every record, filtered however you like, including unannotatable ones. */
  | { name: 'browse' }
  /**
   * `docs/annotation_guidelines.md`, rendered.
   *
   * `at` is a heading slug to scroll to, and it is a **query parameter rather than a second
   * `#`** — the route already lives in the fragment, and a URL cannot carry two. So a link to
   * §6.3 is `#/guidelines?at=6-3-the-hyphen`, not `#/guidelines#6-3-the-hyphen`.
   */
  | { name: 'guidelines'; at: string | null }
  | { name: 'inscription'; inscriptionId: number }
  | { name: 'image'; imageId: number }
  /** The reading-direction task's work surface. One image, two judgements, no canvas. */
  | { name: 'direction'; imageId: number }
  | { name: 'settings' }
  /** Sign in. The only route an unauthenticated visitor is routed *to*. */
  | { name: 'login' }
  /**
   * Redeem an invite. Public, and the token rides in the hash rather than the path.
   *
   * That is not incidental to hash routing — it is the reason to keep it here. A fragment is
   * never sent to the server, so a one-time invite token stays out of CloudFront's access
   * logs, out of any proxy in between, and out of a `Referer` header if the page ever links
   * outward. The same token in a path segment would be in all three.
   */
  | { name: 'accept-invite'; token: string }
  /** Admin: accounts and invites. */
  | { name: 'admin' }
  /** Dev-only box-style gallery — see `components/StyleGallery.svelte`. */
  | { name: 'styles' };

/**
 * `home` is the fallback, not `queue`.
 *
 * Somebody arriving at the bare URL is far more likely to be new than to be resuming: the work
 * queue answers "which stone next?", which is a question you only have once you know what a box
 * is, that an annotator id is required before anything works, and what `----` means. Every
 * existing deep link (`#/queue`, `#/image/12`, `#/inscription/3`, `#/settings`) still parses to
 * exactly what it did before.
 *
 * Which *panel* of home is `lib/homeTabs.ts`'s call, and it is `start` rather than `dashboard`
 * as of 2026-08-09 — the reasoning above argues for orientation, and the dashboard was not it.
 *
 * `#/home/<tab>` addresses a panel. An unknown tab id resolves to the default rather than to
 * nothing: a link from an older build, or a typo, should still land somewhere useful.
 *
 * Exported so the fallback and the deep links can be asserted without a DOM. Callers should use
 * `router.route`; this is the pure function underneath it.
 */
export function parse(hash: string): Route {
  const raw = hash.replace(/^#\/?/, '');
  // The query is split off before the path, so `#/accept-invite?token=abc` does not become a
  // path segment literally called `accept-invite?token=abc`.
  const queryAt = raw.indexOf('?');
  const path = queryAt === -1 ? raw : raw.slice(0, queryAt);
  const query = new URLSearchParams(queryAt === -1 ? '' : raw.slice(queryAt + 1));
  const parts = path.split('/').filter(Boolean);
  if (parts[0] === 'login') return { name: 'login' };
  if (parts[0] === 'accept-invite') {
    return { name: 'accept-invite', token: query.get('token') ?? '' };
  }
  if (parts[0] === 'admin') return { name: 'admin' };
  if (parts[0] === 'home') return { name: 'home', tab: asHomeTab(parts[1]) };
  // `#/queue` is the chooser and `#/queue/<task>` is that task's queue. Every pre-existing
  // `#/queue` bookmark still parses; it now lands on the chooser rather than on a filtered
  // table, which is the change.
  if (parts[0] === 'queue') return { name: 'queue', task: parts[1] ?? null };
  if (parts[0] === 'browse') return { name: 'browse' };
  if (parts[0] === 'guidelines') return { name: 'guidelines', at: query.get('at') };
  if (parts[0] === 'settings') return { name: 'settings' };
  if (parts[0] === 'styles') return { name: 'styles' };
  if (parts[0] === 'inscription' && parts[1]) {
    const id = Number(parts[1]);
    if (Number.isInteger(id)) return { name: 'inscription', inscriptionId: id };
  }
  if (parts[0] === 'image' && parts[1]) {
    const id = Number(parts[1]);
    if (Number.isInteger(id)) return { name: 'image', imageId: id };
  }
  if (parts[0] === 'direction' && parts[1]) {
    const id = Number(parts[1]);
    if (Number.isInteger(id)) return { name: 'direction', imageId: id };
  }
  return { name: 'home', tab: DEFAULT_HOME_TAB };
}

class Router {
  route = $state<Route>(parse(typeof location === 'undefined' ? '' : location.hash));

  start(): () => void {
    const onChange = () => {
      this.route = parse(location.hash);
    };
    window.addEventListener('hashchange', onChange);
    onChange();
    return () => window.removeEventListener('hashchange', onChange);
  }

  /**
   * Navigate, and update `route` immediately rather than waiting for the browser's `hashchange`
   * task. The listener will set the same value a tick later, so this is idempotent — it just
   * means a keyboard-driven tab change repaints in the same frame as the key that caused it,
   * which is also what makes it assertable without waiting on a macrotask.
   */
  go(route: Route): void {
    const target = href(route);
    location.hash = target;
    this.route = parse(target);
  }
}

export function href(route: Route): string {
  switch (route.name) {
    case 'queue':
      return route.task ? `#/queue/${encodeURIComponent(route.task)}` : '#/queue';
    case 'browse':
      return '#/browse';
    case 'guidelines':
      return route.at ? `#/guidelines?at=${encodeURIComponent(route.at)}` : '#/guidelines';
    case 'direction':
      return `#/direction/${route.imageId}`;
    case 'login':
      return '#/login';
    case 'accept-invite':
      return `#/accept-invite?token=${encodeURIComponent(route.token)}`;
    case 'admin':
      return '#/admin';
    case 'settings':
      return '#/settings';
    case 'styles':
      return '#/styles';
    case 'inscription':
      return `#/inscription/${route.inscriptionId}`;
    case 'image':
      return `#/image/${route.imageId}`;
    default:
      // The default tab keeps the bare hash. `#/` is the address people paste, bookmark and
      // type; it should not silently become `#/home/dashboard` the first time somebody clicks
      // the brand, and `parse('#/')` resolves it to exactly this route anyway.
      return route.tab === DEFAULT_HOME_TAB ? '#/' : `#/home/${route.tab}`;
  }
}

export const router = new Router();
